import classNames from 'classnames';
import * as React from 'react';
import type { FormFieldProps } from '../FormFieldProps';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { FormLabel } from '~/Partials/FormLabel/FormLabel';
import type { Theme } from '~/Utils/Theme';

export interface Dictionary<T> {
    [Key: string]: T;
}

type SelectFieldProps = FormFieldProps<string | undefined>;

export interface FormFieldTypeSelectProps
    extends SelectFieldProps,
        Omit<React.SelectHTMLAttributes<HTMLSelectElement>, keyof SelectFieldProps> {
    options: Dictionary<string>;
    label?: string | React.ReactChild;
    defaultValue?: string;
    className?: string;
    theme?: Theme;
    isDisabled?: boolean;
    selectRef?: React.RefObject<HTMLSelectElement>;
}

export const FormFieldTypeSelect: React.FC<React.PropsWithChildren<FormFieldTypeSelectProps>> = React.memo(props => {
    const contextualTheme = useTheme();

    const {
        className,
        label,
        options,
        onChange,
        showErrorMessage,
        errorMessage,
        required,
        defaultValue = 'default',
        theme = contextualTheme,
        isDisabled,
        selectRef,
        ...inputProps
    } = props;

    const { withRoundedCorners } = theme;

    return (
        <div
            className={classNames(
                'Form__field',
                className,
                isDisabled && 'Form__fieldDisabled',
                showErrorMessage && 'Form__fieldError',
                withRoundedCorners && 'Form__fieldRounded'
            )}
        >
            <label>
                <div className="Form__label">
                    <FormLabel label={label} required={required} />
                </div>
                <select
                    ref={selectRef}
                    defaultValue={inputProps.value === undefined ? defaultValue : undefined}
                    className="Form__select"
                    onChange={e => onChange(e.target.value)}
                    required={required}
                    disabled={isDisabled}
                    {...inputProps}
                >
                    {!inputProps.value && (
                        // eslint-disable-next-line jsx-a11y/control-has-associated-label
                        <option className="Form__option" key={defaultValue} value={defaultValue} />
                    )}
                    {Object.keys(options).map(value => (
                        <option className="Form__option" key={value} value={value}>
                            {options[value]}
                        </option>
                    ))}
                </select>
                {showErrorMessage && <span className="Form__errorMessage">{errorMessage}</span>}
            </label>
        </div>
    );
});
